import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { getServicioStyle } from "../../utils/servicioColores";
import AuthImage from "../../components/AuthImage";
import SoloEstaSemana from "../../components/SoloEstaSemana";

/* ─────────────────────────────────────────────
   Constantes
───────────────────────────────────────────── */
const DIAS_LABEL = { l: "Lunes", m: "Martes", x: "Miércoles", j: "Jueves", v: "Viernes", s: "Sábado" };
const DIAS_ORDEN = ["l", "m", "x", "j", "v", "s"];
const TAREAS_LABEL = {
  limpieza_hab: "Limpieza habitación",
  lavado_ropa: "Lavado de ropa",
  planchado: "Planchado",
  banos: "Baños",
  salon: "Salón",
  cocina: "Cocina",
};
const TAREAS_ICONO = {
  limpieza_hab: "🛏️",
  lavado_ropa: "👕",
  planchado: "👔",
  banos: "🚿",
  salon: "🛋️",
  cocina: "🍳",
};

const TAREAS_TODAS = ["limpieza_hab", "lavado_ropa", "planchado", "banos", "salon", "cocina"];

function getLunesDeHoy() {
  const hoy = new Date();
  const d = hoy.getDay();
  const diff = d === 0 ? -6 : 1 - d;
  const lunes = new Date(Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + diff));
  return lunes.toISOString().slice(0, 10);
}

function addSemanas(semana, n) {
  const [y, m, d] = semana.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + 7 * n));
  return date.toISOString().slice(0, 10);
}

function formatSemana(semana) {
  const [y, m, d] = semana.split("-").map(Number);
  const inicio = new Date(Date.UTC(y, m - 1, d));
  const fin = new Date(Date.UTC(y, m - 1, d + 5));
  const opts = { day: "numeric", month: "short", timeZone: "UTC" };
  return `${inicio.toLocaleDateString("es-ES", opts)} – ${fin.toLocaleDateString("es-ES", opts)}`;
}

function isMobileWidth() {
  return window.innerWidth < 768;
}

/* ─────────────────────────────────────────────
   Colores semáforo
───────────────────────────────────────────── */
const SEMAFORO_STYLE = {
  gris:     { bg: "#f8fafc", border: "#e2e8f0", color: "#64748b", label: "⚪ Sin comenzar", badgeBg: "#64748b" },
  amarillo: { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8", label: "🔵 En curso",     badgeBg: "#2563eb" },
  verde:    { bg: "#f0fdf4", border: "#bbf7d0", color: "#15803d", label: "🟢 Cerrado",       badgeBg: "#16a34a" },
  rojo:     { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8", label: "🔵 En curso",     badgeBg: "#2563eb" },
};

/* Calcula colores dinámicos del header según resultados reales */
function getHeaderStyle(s) {
  if (!s.id_ejecucion) return { bg: "#f8fafc", leftBorder: "#94a3b8" };   // gris - sin ejecución
  let nInc = 0;
  try {
    const inc = s.incidencias;
    if (inc) {
      const parsed = typeof inc === 'string' ? JSON.parse(inc) : inc;
      nInc = Array.isArray(parsed) ? parsed.length : 0;
    }
  } catch (e) { /* fallback a 0 */ }
  // Contar tareas "no" hecho (limpieza_hab_ok, lavado_ropa_ok, planchado_ok con valor "no")
  let nHecho = 0;
  if (s.limpieza_hab_ok === "no") nHecho++;
  if (s.lavado_ropa_ok === "no") nHecho++;
  if (s.planchado_ok === "no") nHecho++;
  const nCambios = s.n_cambios     || 0;
  if (nInc > 0)     return { bg: "#fdba74", leftBorder: "#92400e" };       // naranja oscuro - incidencias
  if (nHecho > 0)   return { bg: "#fecaca", leftBorder: "#b91c1c" };       // rojo oscuro - no hecho
  if (nCambios > 0) return { bg: "#fefce8", leftBorder: "#eab308" };       // amarillo - cambios
  if (s.cerrado)    return { bg: "#f0fdf4", leftBorder: "#16a34a" };       // verde - cerrado sin problemas
  return { bg: "#eff6ff", leftBorder: "#2563eb" };                         // azul - en curso sin problemas
}

/* ══════════════════════════════════════════════
   Componente principal
══════════════════════════════════════════════ */
const OrganizarServicioPiso = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const [piso, setPiso] = useState(state?.piso || {});

  // Si venimos de una notificación push (?piso_id=X) sin state, cargamos el piso
  useEffect(() => {
    if (state?.piso) return; // ya tenemos piso por state
    const params = new URLSearchParams(location.search);
    const pisoId = params.get('piso_id');
    if (!pisoId) return;
    axios.get(`/api/servicio/piso/${pisoId}`)
      .then(r => { if (r.data && r.data.id) setPiso(r.data); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [semana, setSemana] = useState(getLunesDeHoy());
  const [plan, setPlan] = useState([]);
  const [semaforo, setSemaforo] = useState([]);
  const [alumnos, setAlumnos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobile, setMobile] = useState(isMobileWidth());
  const [diaAbierto, setDiaAbierto] = useState(null);
  const [modal, setModal] = useState(null);
  const [modalValidacion, setModalValidacion] = useState(null); // { id_seguimiento, dia, tarea }
  const [modalValidacionDia, setModalValidacionDia] = useState(null); // { id_seguimiento, dia }
  const [incidenciasTipos, setIncidenciasTipos] = useState([]); // [{id,emoji,titulo,descripcion}]

  const isSemanaActual = semana === getLunesDeHoy();

  useEffect(() => {
    const onResize = () => setMobile(isMobileWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!piso.id) return;
    axios.get(`/api/servicio/alumnos_piso/${piso.id}`)
      .then(r => setAlumnos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAlumnos([]));
  }, [piso.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios.get(`/api/servicio/incidencias_opciones`)
      .then(r => setIncidenciasTipos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setIncidenciasTipos([]));
  }, []);

  const cargarSemana = useCallback((silencioso = false) => {
    if (!piso.id) return;
    if (!silencioso) setLoading(true);
    Promise.all([
      axios.get(`/api/servicio/plan/${piso.id}/${semana}`),
      axios.get(`/api/servicio/semaforo/${piso.id}/${semana}`),
    ])
      .then(([resPlan, resSem]) => {
        setPlan(Array.isArray(resPlan.data) ? resPlan.data : []);
        setSemaforo(Array.isArray(resSem.data) ? resSem.data : []);
      })
      .catch(() => { setPlan([]); setSemaforo([]); })
      .finally(() => setLoading(false));
  }, [piso.id, semana]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { cargarSemana(); }, [cargarSemana]);

  const guardarCampo = async (dia, campo, valor) => {
    const fila = plan.find(p => p.dia === dia) || {};
    const payload = {
      id_piso: piso.id,
      semana_inicio: semana,
      dia,
      limpieza_hab_id: fila.limpieza_hab_id ?? null,
      lavado_ropa_id:  fila.lavado_ropa_id  ?? null,
      planchado_id:    fila.planchado_id    ?? null,
      banos:           fila.banos           ?? 0,
      salon:           fila.salon           ?? 0,
      cocina:          fila.cocina          ?? 0,
      [campo]: valor,
    };
    try {
      await axios.post("/api/servicio/plan", payload);
      await cargarSemana(true);
    } catch (e) {
      alert(e.response?.data?.error || "Error al guardar");
    }
  };

  const sc = piso.servicio ? getServicioStyle(piso.servicio) : null;

  return (
    <div style={{ maxWidth: mobile ? "100%" : 1100, margin: "0 auto", padding: mobile ? "10px 8px" : "20px 24px", fontFamily: "'Segoe UI', sans-serif" }}>

      {/* CABECERA */}
      <div style={{ marginBottom: 14 }}>
        <button onClick={() => navigate("/supervisora/pisoOrganizarServicios")} style={styles.btnVolver}>
          ← Volver
        </button>
      </div>

      {/* TARJETA INFO PISO */}
      <div style={styles.cardPiso}>
        <div style={styles.cardPisoLeft}>
          <div style={styles.abreviaturaGrande}>{piso.abreviatura}</div>
          {sc && (
            <span style={{ ...styles.badge, background: sc.bg, color: sc.color, border: `1.5px solid ${sc.border}`, fontSize: 13, padding: "4px 12px" }}>
              {piso.servicio}
            </span>
          )}
        </div>
        <div style={styles.cardPisoDivider} />
        <div style={styles.cardPisoRight}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#3b82f6", textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>Asistenta</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{piso.nombre_asistenta || "—"}</div>
          {piso.horas_semana && (
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>{piso.horas_semana} h/semana</div>
          )}
        </div>
      </div>

      {/* SELECTOR DE SEMANA */}
      <div style={styles.selectorSemana}>
        <button onClick={() => setSemana(s => addSemanas(s, -1))} style={styles.btnSemana}>◀</button>
        <div style={{ textAlign: "center", flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: "#1e3a5f" }}>
            {isSemanaActual ? "📅 Esta semana" : "Semana del"}
          </div>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{formatSemana(semana)}</div>
        </div>
        <button onClick={() => setSemana(s => addSemanas(s, 1))} style={styles.btnSemana}>▶</button>
      </div>

      {/* "Solo esta semana": que la organización de esta semana no se copie a las siguientes */}
      {piso.id && <SoloEstaSemana pisoId={piso.id} semana={semana} onCambio={() => cargarSemana(true)} />}

      {loading ? (
        <p style={{ textAlign: "center", color: "#888", marginTop: 40 }}>Cargando…</p>
      ) : mobile ? (
        <VistaMobil
          plan={plan} semaforo={semaforo} alumnos={alumnos} piso={piso} semana={semana}
          diaAbierto={diaAbierto} setDiaAbierto={setDiaAbierto}
          guardarCampo={guardarCampo} setModal={setModal}
          incidenciasTipos={incidenciasTipos}
          onValidar={(id_seguimiento, dia, tarea) => setModalValidacion({ id_seguimiento, dia, tarea })}
          onValidarDia={(id_seguimiento, dia) => setModalValidacionDia({ id_seguimiento, dia })}
        />
      ) : (
        <VistaPC
          plan={plan} semaforo={semaforo} alumnos={alumnos} piso={piso} semana={semana}
          guardarCampo={guardarCampo} setModal={setModal}
          incidenciasTipos={incidenciasTipos}
          onValidar={(id_seguimiento, dia, tarea) => setModalValidacion({ id_seguimiento, dia, tarea })}
          onValidarDia={(id_seguimiento, dia) => setModalValidacionDia({ id_seguimiento, dia })}
        />
      )}

      {modal && (
        <ModalAlumno
          modal={modal}
          alumnos={alumnos}
          onClose={() => setModal(null)}
          onSelect={(id) => { modal.onSelect(id); setModal(null); }}
        />
      )}

      {modalValidacionDia && (
        <ModalValidacionDia
          info={modalValidacionDia}
          incidenciasTipos={incidenciasTipos}
          onClose={() => setModalValidacionDia(null)}
          onGuardado={() => { setModalValidacionDia(null); cargarSemana(true); }}
        />
      )}

      {modalValidacion && (
        <ModalValidacion
          info={modalValidacion}
          plan={plan}
          alumnos={alumnos}
          onClose={() => setModalValidacion(null)}
        />
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════
   CAJA DE INCIDENCIAS (vista cerrada)
══════════════════════════════════════════════ */
const IncidenciasBox = ({ s, incidenciasTipos }) => {
  const ids = Array.isArray(s.incidencias) ? s.incidencias : [];
  const texto = (s.incidencias_texto || "").trim();
  if (!s || (ids.length === 0 && !texto)) return null;
  const tipos = ids.map(id => (incidenciasTipos || []).find(t => Number(t.id) === Number(id))).filter(Boolean);
  return (
    <div style={{ background: "#fff7ed", border: "1.5px solid #fed7aa", borderRadius: 12, padding: "10px 12px" }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#9a3412", marginBottom: 8 }}>⚠️ Incidencias</div>
      {tipos.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: texto ? 8 : 0 }}>
          {tipos.map(t => (
            <span key={t.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#fff", border: "1.5px solid #fdba74", borderRadius: 999, padding: "4px 10px", fontSize: 12, fontWeight: 700, color: "#9a3412" }}>
              <span style={{ fontSize: 14 }}>{t.emoji}</span>{t.titulo}
            </span>
          ))}
        </div>
      )}
      {tipos.length === 0 && ids.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: texto ? 8 : 0 }}>
          <span style={{ background: "#fff", border: "1.5px solid #fdba74", borderRadius: 999, padding: "4px 10px", fontSize: 12, fontWeight: 700, color: "#9a3412" }}>
            {ids.length} incidencia{ids.length > 1 ? 's' : ''}
          </span>
        </div>
      )}
      {texto && (
        <div style={{ fontSize: 13, color: "#7c2d12", background: "#fff", border: "1px solid #fed7aa", borderRadius: 8, padding: "8px 10px", whiteSpace: "pre-wrap" }}>{texto}</div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════
   VISTA MÓVIL
══════════════════════════════════════════════ */
const VistaMobil = ({ plan, semaforo, alumnos, piso, semana, diaAbierto, setDiaAbierto, guardarCampo, setModal, onValidar, onValidarDia, incidenciasTipos }) => {
  const hoy = new Date().toISOString().slice(0, 10);
  // Mostrar todos los días que tiene horario la asistenta
  const diasConDatos = DIAS_ORDEN.filter(d => piso[`${d}_e`] || piso[`${d}_s`] || plan.find(x => x.dia === d));

  if (diasConDatos.length === 0)
    return <p style={{ textAlign: "center", color: "#888", marginTop: 30 }}>Este piso no tiene días de servicio configurados.</p>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {diasConDatos.map(dia => {
        const p = plan.find(x => x.dia === dia) || {};
        const s = semaforo.find(x => x.dia === dia) || {};
        const abierto = diaAbierto === dia;
        const sem = SEMAFORO_STYLE[s.semaforo || "gris"];
        const hdr = getHeaderStyle(s);
        const esHoy = s.fecha_dia === hoy;
        const editable = p.id
          ? !!p.editable
          : (() => { const offset = { l:0,m:1,x:2,j:3,v:4,s:5 }[dia]; const fd = new Date(semana+"T00:00:00"); fd.setDate(fd.getDate()+offset); return fd.toISOString().slice(0,10) >= hoy; })();

        const esCerrado = !!s.cerrado;

        return (
          <div key={dia} style={{ borderRadius: 16, overflow: "hidden", boxShadow: `0 3px 12px rgba(0,0,0,0.10)`, border: `2px solid ${esHoy ? "#2563eb" : hdr.leftBorder}` }}>
            <div onClick={() => setDiaAbierto(abierto ? null : dia)}
              style={{ padding: "14px 16px", cursor: "pointer", background: hdr.bg, borderLeft: `5px solid ${hdr.leftBorder}`, display: "flex", flexDirection: "column", gap: 8 }}>

              {/* Fila 1: Día + HOY + lock + chevron */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 900, fontSize: 22, color: "#0f172a", letterSpacing: "-0.3px" }}>{DIAS_LABEL[dia]}</span>
                  {esHoy && (
                    <span style={{ fontSize: 11, background: "#2563eb", color: "#fff", borderRadius: 20, padding: "2px 9px", fontWeight: 800, letterSpacing: 0.5 }}>HOY</span>
                  )}
                  {!editable && <span style={{ fontSize: 14, opacity: 0.5 }}>🔒</span>}
                </div>
                <span style={{ color: hdr.leftBorder, fontWeight: 700, fontSize: 16 }}>{abierto ? '▲' : '▼'}</span>
              </div>

              {/* Fila 2: Horario */}
              {(piso[`${dia}_e`] || piso[`${dia}_s`]) && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4, alignSelf: "flex-start",
                  background: "rgba(0,0,0,0.06)", borderRadius: 8, padding: "3px 10px" }}>
                  <span style={{ fontSize: 12, color: "#475569", fontWeight: 700 }}>
                    ⏱ {(piso[`${dia}_e`] || "--").slice(0,5)} – {(piso[`${dia}_s`] || "--").slice(0,5)}
                  </span>
                </div>
              )}

              {/* Fila 3: Estado + chips + valoración */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span style={{ background: sem.badgeBg, color: "#fff", borderRadius: 20, padding: "4px 13px", fontSize: 12, fontWeight: 800, letterSpacing: 0.3, flexShrink: 0 }}>{sem.label}</span>
                {!!s.id_ejecucion && (() => {
                  const TAREAS_TODAS_LOCAL = ['limpieza_hab','lavado_ropa','planchado','banos','salon','cocina'];
                  const nSi = TAREAS_TODAS_LOCAL.filter(t => { const v = s[`${t}_ok`]; return v === 'si' || v === 1 || v === true; }).length;
                  const nNo = TAREAS_TODAS_LOCAL.filter(t => { const v = s[`${t}_ok`]; return v === 'no' || v === 0; }).length;
                  const nCambios = (s.cambios_lista || []).length;
                  const nExtras = (s.extras_lista || []).length;
                  const nInc = Array.isArray(s.incidencias) ? s.incidencias.length : ((s.incidencias_texto || "").trim() ? 1 : 0);
                  const chips = [];
                  if (nSi > 0) chips.push({ label: `✅ ${nSi}`, bg: '#dcfce7', c: '#15803d', border: '#4ade80' });
                  if (nNo > 0) chips.push({ label: `❌ ${nNo}`, bg: '#fee2e2', c: '#b91c1c', border: '#f87171' });
                  if (nCambios > 0) chips.push({ label: `🔄 ${nCambios}`, bg: '#fef3c7', c: '#92400e', border: '#fde68a' });
                  if (nExtras > 0) chips.push({ label: `⭐ ${nExtras}`, bg: '#fefce8', c: '#854d0e', border: '#fde047' });
                  if (nInc > 0) chips.push({ label: `⚠️ ${nInc}`, bg: '#fff7ed', c: '#9a3412', border: '#fdba74' });
                  if (chips.length === 0) return null;
                  return chips.map((ch, i) => (
                    <span key={i} style={{ fontSize: 12, fontWeight: 800, background: ch.bg, color: ch.c, border: `1.5px solid ${ch.border}`, borderRadius: 20, padding: '3px 10px', whiteSpace: 'nowrap' }}>{ch.label}</span>
                  ));
                })()}
                {esCerrado && s.valoracion && (
                  <span style={{ fontSize: 15, fontWeight: 800 }}>{s.valoracion === 'bien' ? '👍' : '👎'}</span>
                )}
              </div>

              {/* Fila 4: Cerrado por */}
              {esCerrado && s.cerrado_por && (
                <div style={{ fontSize: 11, color: '#15803d', fontWeight: 700 }}>
                  🔒 {s.cerrado_hora ? `${s.cerrado_hora} · ` : ''}{s.cerrado_por}
                  {s.comentario_cierre && <span style={{ fontWeight: 400, color: '#6b7280' }}> · "{s.comentario_cierre.length > 35 ? s.comentario_cierre.slice(0,35)+'…' : s.comentario_cierre}"</span>}
                </div>
              )}

              {/* Fila 5: Validación del día completo (en cabecera) */}
              {esCerrado && s.id_ejecucion && (
                <div onClick={e => e.stopPropagation()}>
                  <ValidarDiaBoton
                    idSeguimiento={s.id_ejecucion}
                    dia={dia}
                    onAbrir={() => onValidarDia(s.id_ejecucion, dia)}
                  />
                </div>
              )}
            </div>

            {abierto && (
              <div style={{ background: "#fff", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                {esCerrado && s.valoracion && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 14, padding: "16px 18px",
                    borderRadius: 14, marginBottom: 4,
                    background: s.valoracion === 'bien' ? "#f0fdf4" : "#fff1f2",
                    border: `2px solid ${s.valoracion === 'bien' ? "#4ade80" : "#fca5a5"}`,
                  }}>
                    <span style={{ fontSize: 44, lineHeight: 1 }}>{s.valoracion === 'bien' ? '👍' : '👎'}</span>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: s.valoracion === 'bien' ? '#15803d' : '#b91c1c' }}>
                        {s.valoracion === 'bien' ? 'Día bien' : 'Día mal'}
                      </div>
                      {s.comentario_cierre && (
                        <div style={{ fontSize: 14, color: '#374151', marginTop: 3, fontStyle: 'italic' }}>
                          "{s.comentario_cierre}"
                        </div>
                      )}
                      {s.cerrado_por && (
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                          🔒 {s.cerrado_por}{s.cerrado_hora ? ` · ${s.cerrado_hora}h` : ''}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {["limpieza_hab", "lavado_ropa", "planchado"].map(tarea => {
                  const campoId = `${tarea}_id`;
                  const alumnoId = p[campoId];
                  const alumno = alumnos.find(a => a.id_alumno === alumnoId);
                  const tareaHecha = !!s.id_ejecucion && s[`${tarea}_ok`] && s[`${tarea}_ok`] !== null;
                  const valEstado = tareaHecha ? s[`val_${tarea}`] : undefined; // null|0|1
                  const valBg = valEstado === 1 ? "#bbf7d0" : valEstado === 0 ? "#fecaca" : "#e0f2fe";
                  const valColor = valEstado === 0 ? "#b91c1c" : "#166534";
                  const valBorder = valEstado === 0 ? "#f87171" : "#4ade80";
                  const valIcon = valEstado === 1 ? "✅" : valEstado === 0 ? "❌" : "🔍";
                  const okEstado = s[`${tarea}_ok`] || null;
                  const rowBg = okEstado === 'si' ? '#dcfce7' : okEstado === 'no' ? '#fee2e2' : okEstado === 'cambio' ? '#fefce8' : '#f8fafc';
                  const rowBorderColor = okEstado === 'si' ? '#4ade80' : okEstado === 'no' ? '#f87171' : okEstado === 'cambio' ? '#fde68a' : '#e2e8f0';
                  return (
                    <div key={tarea} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 10, background: rowBg, border: `1px solid ${rowBorderColor}` }}>
                      <span style={{ fontSize: 18 }}>{TAREAS_ICONO[tarea]}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{TAREAS_LABEL[tarea]}</div>
                        {alumno
                          ? <div style={{ fontSize: 12, color: "#3b82f6" }}>{alumno.habitacion} · {alumno.nombre}</div>
                          : <div style={{ fontSize: 12, color: "#9ca3af" }}>Sin asignar</div>}
                        {!!s.id_ejecucion && s[`${tarea}_ok`] && (() => {
                          const ok = s[`${tarea}_ok`];
                          const cambio = ok === 'cambio' ? (s.cambios_lista || []).find(c => c.tarea_original === tarea) : null;
                          const razon = ok === 'no' ? s[`${tarea}_razon`] : null;
                          const bg = ok === 'si' ? '#dcfce7' : ok === 'no' ? '#fee2e2' : '#fef3c7';
                          const color = ok === 'si' ? '#15803d' : ok === 'no' ? '#dc2626' : '#92400e';
                          return <div style={{ marginTop: 2 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color, background: bg, borderRadius: 6, padding: '1px 6px', display: 'inline-block' }}>{ok === 'si' ? '✅ Hecho' : ok === 'no' ? '❌ No hecho' : `🔄 ${cambio ? (cambio.hab ? cambio.hab : (TAREAS_LABEL[cambio.tarea_real] || cambio.tarea_real)) : 'Cambio'}`}</div>
                            {razon && <div style={{ fontSize: 10, color: '#b91c1c', fontWeight: 600, marginTop: 1 }}>{razon}</div>}
                          </div>;
                        })()}
                      </div>
                      {editable && (
                        <button onClick={() => setModal({ titulo: `${TAREAS_LABEL[tarea]} — ${DIAS_LABEL[dia]}`, currentId: alumnoId, onSelect: (id) => guardarCampo(dia, campoId, id) })} style={styles.btnEditar}>✏️</button>
                      )}
                      {tareaHecha && s.id_ejecucion && (
                        <button onClick={() => onValidar(s.id_ejecucion, dia, tarea)} style={{ ...styles.btnValidar, background: valBg, color: valColor, border: `1px solid ${valBorder}` }}>
                          {valIcon}{s[`n_fotos_${tarea}`] > 0 && ` 📷${s[`n_fotos_${tarea}`]}`}
                        </button>
                      )}
                    </div>
                  );
                })}

                {["banos", "salon", "cocina"].map(tarea => {
                  const activo = !!p[tarea];
                  const tareaHecha = !!s.id_ejecucion && s[`${tarea}_ok`] && s[`${tarea}_ok`] !== null;
                  const valEstado = tareaHecha ? s[`val_${tarea}`] : undefined;
                  const valBg = valEstado === 1 ? "#bbf7d0" : valEstado === 0 ? "#fecaca" : "#e0f2fe";
                  const valColor = valEstado === 0 ? "#b91c1c" : "#166534";
                  const valBorder = valEstado === 0 ? "#f87171" : "#4ade80";
                  const valIcon = valEstado === 1 ? "✅" : valEstado === 0 ? "❌" : "🔍";
                  const okEstado = s[`${tarea}_ok`] || null;
                  const rowBg = okEstado === 'si' ? '#dcfce7' : okEstado === 'no' ? '#fee2e2' : okEstado === 'cambio' ? '#fefce8' : (activo ? "#d1fae5" : "#f8fafc");
                  const rowBorderColor = okEstado === 'si' ? '#4ade80' : okEstado === 'no' ? '#f87171' : okEstado === 'cambio' ? '#fde68a' : (activo ? "#34d399" : "#e2e8f0");
                  return (
                    <div key={tarea} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10, background: rowBg, border: `1px solid ${rowBorderColor}` }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: 15, fontWeight: 600, color: activo ? "#065f46" : "#6b7280" }}>{TAREAS_ICONO[tarea]} {TAREAS_LABEL[tarea]}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        {!!s.id_ejecucion && s[`${tarea}_ok`] && (() => {
                          const ok = s[`${tarea}_ok`];
                          const bg = ok === 'si' ? '#dcfce7' : ok === 'no' ? '#fee2e2' : '#fef3c7';
                          const color = ok === 'si' ? '#15803d' : ok === 'no' ? '#dc2626' : '#92400e';
                          return <div style={{ fontSize: 11, fontWeight: 700, color, background: bg, borderRadius: 6, padding: '1px 6px', display: 'inline-block', whiteSpace: 'nowrap' }}>{ok === 'si' ? '✅ Hecho' : ok === 'no' ? '❌ No hecho' : '🔄 Cambio'}</div>;
                        })()}
                        <button disabled={!editable} onClick={() => editable && guardarCampo(dia, tarea, activo ? 0 : 1)}
                          style={{ ...styles.btnToggle, background: activo ? "#10b981" : "#d1d5db", color: activo ? "#fff" : "#6b7280", opacity: editable ? 1 : 0.5, cursor: editable ? "pointer" : "default", fontSize: 15, padding: "8px 18px" }}>
                          {activo ? "✓ Sí" : "No"}
                        </button>
                      </div>
                      {tareaHecha && s.id_ejecucion && (
                        <button onClick={() => onValidar(s.id_ejecucion, dia, tarea)} style={{ ...styles.btnValidar, background: valBg, color: valColor, border: `1px solid ${valBorder}` }}>
                          {valIcon}{s[`n_fotos_${tarea}`] > 0 && ` 📷${s[`n_fotos_${tarea}`]}`}
                        </button>
                      )}
                    </div>
                  );
                })}

                {(s.extras_lista || []).length > 0 && (
                  <div style={{ marginTop: 4, padding: '8px 10px', borderRadius: 10, background: '#fefce8', border: '1.5px solid #fde047' }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#854d0e', marginBottom: 6 }}>⭐ Extras ({(s.extras_lista || []).length})</div>
                    {(s.extras_lista || []).map((ex, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 8, background: '#fff', border: '1px solid #fde047', marginBottom: 5 }}>
                        <span style={{ fontSize: 17 }}>{TAREAS_ICONO[ex.tarea] || '⭐'}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#78350f' }}>{TAREAS_LABEL[ex.tarea] || ex.tarea}</div>
                          {ex.hab && <div style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600 }}>{ex.hab}{ex.nombre ? ` · ${ex.nombre}` : ''}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!editable && (
                  <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center" }}>🔒 Día bloqueado (pasado o en ejecución)</div>
                )}

                <IncidenciasBox s={s} incidenciasTipos={incidenciasTipos} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ══════════════════════════════════════════════
   VISTA PC — tabla estilo imagen adjunta
══════════════════════════════════════════════ */
const VistaPC = ({ plan, semaforo, alumnos, piso, semana, guardarCampo, setModal, onValidar, onValidarDia, incidenciasTipos }) => {
  const hoy = new Date().toISOString().slice(0, 10);
  const diasConDatos = DIAS_ORDEN.filter(d => piso[`${d}_e`] || piso[`${d}_s`] || plan.find(x => x.dia === d));

  if (diasConDatos.length === 0)
    return <p style={{ textAlign: "center", color: "#888", marginTop: 30 }}>Este piso no tiene días de servicio configurados.</p>;

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={styles.tabla}>
        <thead>
          <tr>
            <th style={{ ...styles.th, textAlign: "left", paddingLeft: 14, width: 170, background: "#1e3a5f", color: "#fff" }}>Tarea</th>
            {diasConDatos.map(dia => {
              const s = semaforo.find(x => x.dia === dia) || {};
              const sem = SEMAFORO_STYLE[s.semaforo || "gris"];
              const hdr = getHeaderStyle(s);
              const esHoy = s.fecha_dia === hoy;
              return (
                <th key={dia} style={{ ...styles.th, background: hdr.bg, borderBottom: `3px solid ${hdr.leftBorder}`, minWidth: 155, padding: '16px 12px', verticalAlign: 'top' }}>
                  {/* Día + HOY */}
                  <div style={{ fontWeight: 900, fontSize: 17, color: '#0f172a', marginBottom: 4 }}>{DIAS_LABEL[dia]}</div>
                  {esHoy && <div style={{ fontSize: 11, background: "#2563eb", color: "#fff", borderRadius: 20, padding: "2px 9px", display: "inline-block", marginBottom: 4, fontWeight: 800 }}>HOY</div>}
                  {(piso[`${dia}_e`] || piso[`${dia}_s`]) && (
                    <div style={{ fontSize: 12, marginBottom: 8, fontWeight: 700, color: '#475569' }}>
                      ⏱ {(piso[`${dia}_e`] || "--").slice(0,5)}–{(piso[`${dia}_s`] || "--").slice(0,5)}
                    </div>
                  )}
                  {/* Estado badge — sólido y llamativo */}
                  <div style={{
                    display: 'inline-block',
                    background: sem.badgeBg, color: '#fff',
                    borderRadius: 20, padding: '5px 14px',
                    fontSize: 12, fontWeight: 900, letterSpacing: 0.3, marginBottom: 6
                  }}>{sem.label}</div>
                  {/* Chips de stats */}
                  {!!s.id_ejecucion && (() => {
                    const nSi = TAREAS_TODAS.filter(t => s[`${t}_ok`] === 'si').length;
                    const nNo = TAREAS_TODAS.filter(t => s[`${t}_ok`] === 'no').length;
                    const nCambios = (s.cambios_lista || []).length;
                    const nExtras = (s.extras_lista || []).length;
                    const nInc = Array.isArray(s.incidencias) ? s.incidencias.length : ((s.incidencias_texto || "").trim() ? 1 : 0);
                    const chips = [];
                    if (nSi > 0) chips.push({ label: `✅ ${nSi}`, bg: '#dcfce7', c: '#15803d', border: '#4ade80' });
                    if (nNo > 0) chips.push({ label: `❌ ${nNo}`, bg: '#fee2e2', c: '#b91c1c', border: '#f87171' });
                    if (nCambios > 0) chips.push({ label: `🔄 ${nCambios}`, bg: '#fef3c7', c: '#92400e', border: '#fde68a' });
                    if (nExtras > 0) chips.push({ label: `⭐ ${nExtras}`, bg: '#fefce8', c: '#854d0e', border: '#fde047' });
                    if (nInc > 0) chips.push({ label: `⚠️ ${nInc}`, bg: '#fff7ed', c: '#9a3412', border: '#fdba74' });
                    if (chips.length === 0) return null;
                    return (
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                        {chips.map((ch, i) => (
                          <span key={i} style={{ fontSize: 11, fontWeight: 800, background: ch.bg, color: ch.c, border: `1.5px solid ${ch.border}`, borderRadius: 20, padding: '2px 8px', whiteSpace: 'nowrap' }}>{ch.label}</span>
                        ))}
                      </div>
                    );
                  })()}
                  {!!s.cerrado && s.valoracion && (
                    <div style={{ fontSize: 13, fontWeight: 800, marginTop: 4 }}>{s.valoracion === 'bien' ? '👍 Bien' : '👎 Mal'}</div>
                  )}
                  {!!s.cerrado && s.cerrado_por && (
                    <div style={{ fontSize: 10, color: sem.color, marginTop: 3, fontWeight: 700, opacity: 0.85 }}>
                      🔒{s.cerrado_hora ? ` ${s.cerrado_hora}` : ''} {s.cerrado_por}
                    </div>
                  )}
                  {!!s.cerrado && s.id_ejecucion && (
                    <div style={{ marginTop: 8 }}>
                      <ValidarDiaBoton
                        idSeguimiento={s.id_ejecucion}
                        dia={dia}
                        onAbrir={() => onValidarDia(s.id_ejecucion, dia)}
                      />
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {["limpieza_hab", "lavado_ropa", "planchado", "banos", "salon", "cocina"].map((tarea, ti) => (
            <tr key={tarea} style={{ background: ti % 2 === 0 ? "#f9fafb" : "#fff" }}>
              <td style={{ ...styles.td, fontWeight: 600, color: "#374151", fontSize: 16, textAlign: "left", paddingLeft: 14 }}>
                {TAREAS_ICONO[tarea]} {TAREAS_LABEL[tarea]}
              </td>
              {diasConDatos.map(dia => {
                const p = plan.find(x => x.dia === dia) || {};
                const s = semaforo.find(x => x.dia === dia) || {};
                const esCerrado = !!s.cerrado;
                const editable = p.id
                  ? !!p.editable
                  : (() => { const offset = { l:0,m:1,x:2,j:3,v:4,s:5 }[dia]; const fd = new Date(semana+"T00:00:00"); fd.setDate(fd.getDate()+offset); return fd.toISOString().slice(0,10) >= hoy; })();
                const esToggle = tarea === "banos" || tarea === "salon" || tarea === "cocina";

                if (esToggle) {
                  const activo = !!p[tarea];
                  const tareaHecha = !!s.id_ejecucion && s[`${tarea}_ok`] && s[`${tarea}_ok`] !== null;
                  const valEstado = tareaHecha ? s[`val_${tarea}`] : undefined;
                  const valBg = valEstado === 1 ? "#bbf7d0" : valEstado === 0 ? "#fecaca" : "#e0f2fe";
                  const valColor = valEstado === 0 ? "#b91c1c" : "#166534";
                  const valBorder = valEstado === 0 ? "#f87171" : "#4ade80";
                  const valIcon = valEstado === 1 ? "✅" : valEstado === 0 ? "❌" : "🔍";
                  const okEstadoT = s[`${tarea}_ok`] || null;
                  const tdBg = okEstadoT === 'si' ? '#dcfce7' : okEstadoT === 'no' ? '#fee2e2' : okEstadoT === 'cambio' ? '#fefce8' : undefined;
                  return (
                    <td key={dia} style={{ ...styles.td, background: tdBg }}>
                      {!!s.id_ejecucion && s[`${tarea}_ok`] && (() => {
                        const ok = s[`${tarea}_ok`];
                        const bg = ok === 'si' ? '#dcfce7' : ok === 'no' ? '#fee2e2' : '#fef3c7';
                        const color = ok === 'si' ? '#15803d' : ok === 'no' ? '#dc2626' : '#92400e';
                        return <div style={{ fontSize: 11, fontWeight: 700, color, background: bg, borderRadius: 6, padding: '2px 6px', marginBottom: 4, display: 'inline-block' }}>{ok === 'si' ? '✅ Hecho' : ok === 'no' ? '❌ No hecho' : '🔄 Cambio'}</div>;
                      })()}
                      <button disabled={!editable}
                        onClick={() => editable && guardarCampo(dia, tarea, activo ? 0 : 1)}
                        style={{ ...styles.btnToggle, background: activo ? "#10b981" : "#d1d5db", color: activo ? "#fff" : "#6b7280", opacity: editable ? 1 : 0.5, cursor: editable ? "pointer" : "default", fontSize: 15, padding: "8px 16px" }}>
                        {activo ? "✓ Sí" : "—"}
                      </button>
                      {tareaHecha && s.id_ejecucion && (
                        <div style={{ marginTop: 6 }}>
                          <button onClick={() => onValidar(s.id_ejecucion, dia, tarea)} style={{ ...styles.btnValidar, background: valBg, color: valColor, border: `1px solid ${valBorder}` }}>{valIcon} {s[`n_fotos_${tarea}`] > 0 && `📷${s[`n_fotos_${tarea}`]} `}Ver</button>
                        </div>
                      )}
                    </td>
                  );
                }

                const campoId = `${tarea}_id`;
                const alumnoId = p[campoId];
                const alumno = alumnos.find(a => a.id_alumno === alumnoId);
                const tareaHecha = !!s.id_ejecucion && s[`${tarea}_ok`] && s[`${tarea}_ok`] !== null;
                const valEstado = tareaHecha ? s[`val_${tarea}`] : undefined;
                const valBg = valEstado === 1 ? "#bbf7d0" : valEstado === 0 ? "#fecaca" : "#e0f2fe";
                const valColor = valEstado === 0 ? "#b91c1c" : "#166534";
                const valBorder = valEstado === 0 ? "#f87171" : "#4ade80";
                const valIcon = valEstado === 1 ? "✅" : valEstado === 0 ? "❌" : "🔍";
                const tdBg = s[`${tarea}_ok`] === 'si' ? '#dcfce7' : s[`${tarea}_ok`] === 'no' ? '#fee2e2' : s[`${tarea}_ok`] === 'cambio' ? '#fefce8' : undefined;

                return (
                  <td key={dia} style={{ ...styles.td, cursor: editable ? "pointer" : "default", background: tdBg }}
                    onClick={() => {
                      if (!editable) return;
                      setModal({ titulo: `${TAREAS_LABEL[tarea]} — ${DIAS_LABEL[dia]}`, currentId: alumnoId, onSelect: (id) => guardarCampo(dia, campoId, id) });
                    }}>
                    {alumno ? (
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#1e3a5f" }}>{alumno.habitacion}</div>
                        <div style={{ fontSize: 13, color: "#64748b" }}>{alumno.nombre}</div>
                        {editable && <div style={{ fontSize: 11, color: "#3b82f6", marginTop: 2 }}>✏️ editar</div>}
                      </div>
                    ) : (
                      <div style={{ textAlign: "center", color: editable ? "#3b82f6" : "#cbd5e1", fontSize: 14 }}>
                        {editable ? "+ Asignar" : "—"}
                      </div>
                    )}
                    {!!s.id_ejecucion && s[`${tarea}_ok`] && (() => {
                      const ok = s[`${tarea}_ok`];
                      const cambio = ok === 'cambio' ? (s.cambios_lista || []).find(c => c.tarea_original === tarea) : null;
                      const razon = ok === 'no' ? s[`${tarea}_razon`] : null;
                      const bg = ok === 'si' ? '#dcfce7' : ok === 'no' ? '#fee2e2' : '#fef3c7';
                      const color = ok === 'si' ? '#15803d' : ok === 'no' ? '#dc2626' : '#92400e';
                      return (
                        <div style={{ marginTop: 4 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color, background: bg, borderRadius: 6, padding: '2px 6px', display: 'inline-block' }}>
                            {ok === 'si' ? '✅ Hecho' : ok === 'no' ? '❌ No hecho' : `🔄 ${cambio ? (cambio.hab ? cambio.hab : (TAREAS_LABEL[cambio.tarea_real] || cambio.tarea_real)) : 'Cambio'}`}
                          </div>
                          {razon && <div style={{ fontSize: 10, color: '#b91c1c', fontWeight: 600, marginTop: 1 }}>{razon}</div>}
                        </div>
                      );
                    })()}
                    {tareaHecha && s.id_ejecucion && (
                      <div style={{ marginTop: 6, textAlign: "center" }} onClick={e => { e.stopPropagation(); onValidar(s.id_ejecucion, dia, tarea); }}>
                        <button style={{ ...styles.btnValidar, background: valBg, color: valColor, border: `1px solid ${valBorder}` }}>{valIcon} {s[`n_fotos_${tarea}`] > 0 && `📷${s[`n_fotos_${tarea}`]} `}Ver</button>
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
          {diasConDatos.some(dia => {
            const s = semaforo.find(x => x.dia === dia) || {};
            return (s.extras_lista || []).length > 0;
          }) && (
            <tr style={{ background: '#fefce8' }}>
              <td style={{ ...styles.td, fontWeight: 700, color: '#854d0e', fontSize: 14, textAlign: 'left', paddingLeft: 14 }}>
                ⭐ Extras ({diasConDatos.reduce((acc, dia) => { const s = semaforo.find(x => x.dia === dia) || {}; return acc + (s.extras_lista ? s.extras_lista.length : 0); }, 0)})
              </td>
              {diasConDatos.map(dia => {
                const s = semaforo.find(x => x.dia === dia) || {};
                const extras = s.extras_lista || [];
                return (
                  <td key={dia} style={{ ...styles.td, verticalAlign: 'top' }}>
                    {extras.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {extras.map((ex, i) => (
                          <div key={i} style={{ background: '#fff', border: '1px solid #fde047', borderRadius: 7, padding: '5px 8px', textAlign: 'left' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#78350f' }}>{TAREAS_ICONO[ex.tarea] || '⭐'} {TAREAS_LABEL[ex.tarea] || ex.tarea}</div>
                            {ex.hab && <div style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600 }}>{ex.hab}{ex.nombre ? ` · ${ex.nombre}` : ''}</div>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>
                    )}
                  </td>
                );
              })}
            </tr>
          )}
          {diasConDatos.some(dia => {
            const s = semaforo.find(x => x.dia === dia) || {};
            return (Array.isArray(s.incidencias) && s.incidencias.length > 0) || !!(s.incidencias_texto || "").trim();
          }) && (
            <tr style={{ background: "#fff7ed" }}>
              <td style={{ ...styles.td, fontWeight: 700, color: "#9a3412", fontSize: 14, textAlign: "left", paddingLeft: 14 }}>
                ⚠️ Incidencias
              </td>
              {diasConDatos.map(dia => {
                const s = semaforo.find(x => x.dia === dia) || {};
                const ids = Array.isArray(s.incidencias) ? s.incidencias : [];
                const texto = (s.incidencias_texto || "").trim();
                const tipos = ids.map(id => (incidenciasTipos || []).find(t => Number(t.id) === Number(id))).filter(Boolean);
                return (
                  <td key={dia} style={{ ...styles.td, verticalAlign: "top" }}>
                    {(tipos.length > 0 || ids.length > 0 || texto) ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
                        {tipos.map(t => (
                          <span key={t.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#fff", border: "1.5px solid #fdba74", borderRadius: 999, padding: "3px 8px", fontSize: 11, fontWeight: 700, color: "#9a3412" }}>
                            <span style={{ fontSize: 13 }}>{t.emoji}</span>{t.titulo}
                          </span>
                        ))}
                        {tipos.length === 0 && ids.length > 0 && (
                          <span style={{ background: "#fff", border: "1.5px solid #fdba74", borderRadius: 999, padding: "3px 8px", fontSize: 11, fontWeight: 700, color: "#9a3412" }}>
                            {ids.length} incidencia{ids.length > 1 ? 's' : ''}
                          </span>
                        )}
                        {texto && (
                          <div style={{ fontSize: 12, color: "#7c2d12", whiteSpace: "pre-wrap", textAlign: "left" }}>{texto}</div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>
                    )}
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
const ModalAlumno = ({ modal, alumnos, onClose, onSelect }) => (
  <div style={styles.overlay} onClick={onClose}>
    <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span style={{ fontWeight: 700, fontSize: 16, color: "#1e3a5f" }}>{modal.titulo || "Seleccionar habitación"}</span>
        <button onClick={onClose} style={styles.btnCerrarModal}>✕</button>
      </div>
      <div onClick={() => onSelect(null)}
        style={{ ...styles.opcionAlumno, background: modal.currentId == null ? "#dbeafe" : "#f9fafb", fontStyle: "italic", color: "#6b7280" }}>
        — Sin asignar
      </div>
      {alumnos.map(a => (
        <div key={a.id_alumno} onClick={() => onSelect(a.id_alumno)}
          style={{ ...styles.opcionAlumno, background: modal.currentId === a.id_alumno ? "#dbeafe" : "#f9fafb" }}>
          <span style={{ fontWeight: 700, color: "#1e3a5f", minWidth: 38 }}>{a.habitacion}</span>
          <span style={{ color: "#374151", fontSize: 14, flex: 1 }}>{a.nombre}</span>
          {modal.currentId === a.id_alumno && <span style={{ color: "#3b82f6", fontWeight: 700 }}>✓</span>}
        </div>
      ))}
    </div>
  </div>
);

/* ══════════════════════════════════════════════
   BOTÓN VALIDAR DÍA (muestra estado actual)
══════════════════════════════════════════════ */
const ValidarDiaBoton = ({ idSeguimiento, dia, onAbrir }) => {
  const [val, setVal] = useState(null); // null=cargando, {validado,validado_por}
  useEffect(() => {
    axios.get(`/api/servicio/validacion-dia/${idSeguimiento}`)
      .then(r => setVal(r.data))
      .catch(() => setVal({}));
  }, [idSeguimiento]);

  if (val === null) return null;
  const validado = val?.validado;
  const bg = validado === 1 ? '#f0fdf4' : validado === 0 ? '#fff1f2' : '#f0f9ff';
  const color = validado === 1 ? '#15803d' : validado === 0 ? '#b91c1c' : '#0369a1';
  const icon = validado === 1 ? '✅' : validado === 0 ? '❌' : '🔍';
  const label = validado === 1 ? 'Día validado' : validado === 0 ? 'Día no válido' : 'Validar día';
  const nIncs = (val?.incidencias || []).length;
  return (
    <button onClick={onAbrir} style={{
      width: '100%', padding: '10px 14px', borderRadius: 12, border: `2px solid ${color}`,
      background: bg, color, fontWeight: 700, fontSize: 14, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    }}>
      <span>{icon}</span>
      <span>{label}</span>
      {nIncs > 0 && <span style={{ background: color, color: '#fff', borderRadius: 20, padding: '1px 8px', fontSize: 11 }}>⚠️ {nIncs}</span>}
      {val?.validado_por && validado !== null && (
        <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}> · {val.validado_por}</span>
      )}
    </button>
  );
};

/* ══════════════════════════════════════════════
   MODAL VALIDACIÓN DEL DÍA COMPLETO
══════════════════════════════════════════════ */
const ModalValidacionDia = ({ info, incidenciasTipos, onClose, onGuardado }) => {
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [validado, setValidado] = useState(null); // null | 0 | 1
  const [incidencias, setIncidencias] = useState([]); // [{id_opcion, texto}]
  const [notas, setNotas] = useState('');
  const [validadoPor, setValidadoPor] = useState(null);
  const [validadoAt, setValidadoAt] = useState(null);

  useEffect(() => {
    setCargando(true);
    axios.get(`/api/servicio/validacion-dia/${info.id_seguimiento}`)
      .then(r => {
        setValidado(r.data.validado ?? null);
        setIncidencias(Array.isArray(r.data.incidencias) ? r.data.incidencias : []);
        setNotas(r.data.notas || '');
        setValidadoPor(r.data.validado_por || null);
        setValidadoAt(r.data.validado_at || null);
      })
      .catch(() => {})
      .finally(() => setCargando(false));
  }, [info.id_seguimiento]);

  const toggleIncOpcion = (id) => {
    setIncidencias(prev => {
      const idx = prev.findIndex(i => i.id_opcion === id);
      if (idx >= 0) return prev.filter((_, j) => j !== idx);
      return [...prev, { id_opcion: id, texto: '' }];
    });
  };
  const setTextoInc = (id, texto) => {
    setIncidencias(prev => prev.map(i => i.id_opcion === id ? { ...i, texto } : i));
  };

  const guardar = async (valor) => {
    setGuardando(true);
    try {
      const res = await axios.post('/api/servicio/validacion-dia', {
        id_seguimiento: info.id_seguimiento,
        validado: valor,
        incidencias,
        notas,
      });
      setValidado(res.data.validado);
      setValidadoPor(res.data.validado_por);
      setValidadoAt(res.data.validado_at);
      onGuardado();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const estadoBg = validado === 1 ? '#f0fdf4' : validado === 0 ? '#fff1f2' : '#f8fafc';
  const estadoBorder = validado === 1 ? '#86efac' : validado === 0 ? '#fca5a5' : '#e5e7eb';
  const estadoColor = validado === 1 ? '#15803d' : validado === 0 ? '#b91c1c' : '#374151';
  const estadoEmoji = validado === 1 ? '✅' : validado === 0 ? '❌' : '⏳';
  const estadoTexto = validado === 1 ? 'Día validado' : validado === 0 ? 'Día no válido' : 'Pendiente de validación';

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, color: '#1e3a5f' }}>📋 Validar día completo</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{DIAS_LABEL[info.dia]}</div>
          </div>
          <button onClick={onClose} style={styles.btnCerrarModal}>✕</button>
        </div>

        {cargando ? <p style={{ textAlign: 'center', color: '#888' }}>Cargando…</p> : (
          <>
            {/* Estado actual */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
              borderRadius: 14, marginBottom: 16,
              background: estadoBg, border: `2px solid ${estadoBorder}`,
            }}>
              <span style={{ fontSize: 28 }}>{estadoEmoji}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: estadoColor }}>{estadoTexto}</div>
                {validadoAt && validado !== null && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    {new Date(validadoAt).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    {validadoPor && ` · ${validadoPor}`}
                  </div>
                )}
              </div>
            </div>

            {/* Botones validar */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              <button onClick={() => guardar(1)} disabled={guardando} style={{
                flex: 1, padding: '11px 0', borderRadius: 12, fontWeight: 800, fontSize: 14,
                cursor: guardando ? 'not-allowed' : 'pointer',
                background: validado === 1 ? 'linear-gradient(135deg,#16a34a,#15803d)' : '#f0fdf4',
                color: validado === 1 ? '#fff' : '#15803d',
                border: `2px solid ${validado === 1 ? '#15803d' : '#86efac'}`,
                opacity: guardando ? 0.6 : 1,
              }}>✓ Día válido</button>
              <button onClick={() => guardar(0)} disabled={guardando} style={{
                flex: 1, padding: '11px 0', borderRadius: 12, fontWeight: 800, fontSize: 14,
                cursor: guardando ? 'not-allowed' : 'pointer',
                background: validado === 0 ? 'linear-gradient(135deg,#dc2626,#b91c1c)' : '#fff1f2',
                color: validado === 0 ? '#fff' : '#dc2626',
                border: `2px solid ${validado === 0 ? '#b91c1c' : '#fca5a5'}`,
                opacity: guardando ? 0.6 : 1,
              }}>✗ No válido</button>
              {validado !== null && (
                <button onClick={() => guardar(null)} disabled={guardando} style={{
                  padding: '11px 14px', borderRadius: 12, border: '2px solid #e5e7eb',
                  fontWeight: 700, fontSize: 13, cursor: guardando ? 'not-allowed' : 'pointer',
                  background: '#f9fafb', color: '#6b7280', opacity: guardando ? 0.6 : 1,
                }} title="Restablecer pendiente">↩</button>
              )}
            </div>

            {/* Incidencias */}
            {incidenciasTipos.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 8 }}>⚠️ Incidencias del día</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {incidenciasTipos.map(op => {
                    const sel = incidencias.find(i => i.id_opcion === op.id);
                    return (
                      <button key={op.id} onClick={() => toggleIncOpcion(op.id)} style={{
                        padding: '6px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        background: sel ? '#fef3c7' : '#f9fafb',
                        border: `2px solid ${sel ? '#f59e0b' : '#e5e7eb'}`,
                        color: sel ? '#92400e' : '#374151',
                      }}>
                        {op.emoji} {op.titulo}
                      </button>
                    );
                  })}
                </div>
                {incidencias.map(inc => {
                  const op = incidenciasTipos.find(t => t.id === inc.id_opcion);
                  return (
                    <div key={inc.id_opcion} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 3 }}>
                        {op?.emoji} {op?.titulo} — detalle (opcional)
                      </div>
                      <input value={inc.texto || ''} onChange={e => setTextoInc(inc.id_opcion, e.target.value)}
                        placeholder="Describe la incidencia..." style={{
                          width: '100%', padding: '7px 10px', borderRadius: 8,
                          border: '1.5px solid #fde68a', fontSize: 13, boxSizing: 'border-box',
                        }} />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Notas */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 6 }}>📝 Notas</div>
              <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={3}
                placeholder="Añade notas sobre la validación..." style={{
                  width: '100%', padding: '8px 10px', borderRadius: 8,
                  border: '1.5px solid #e5e7eb', fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
                }} />
            </div>

            <button onClick={() => guardar(validado ?? 1)} disabled={guardando} style={{
              width: '100%', padding: '12px 0', borderRadius: 12,
              background: '#6366f1', color: '#fff', border: 'none',
              fontWeight: 800, fontSize: 15, cursor: guardando ? 'not-allowed' : 'pointer',
              opacity: guardando ? 0.7 : 1,
            }}>
              {guardando ? '⏳ Guardando...' : '💾 Guardar validación'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════
   MODAL VALIDACIÓN DE TAREA
══════════════════════════════════════════════ */
const ModalValidacion = ({ info, plan, alumnos, onClose }) => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setCargando(true);
    axios.get(`/api/servicio/validaciones/${info.id_seguimiento}`)
      .then(r => setDatos(r.data))
      .catch(() => setDatos(null))
      .finally(() => setCargando(false));
  }, [info.id_seguimiento]);

  const fotos = datos?.fotos_por_tarea?.[info.tarea] || [];

  // Alumno asignado (solo para tareas con habitación)
  const esToggle = info.tarea === "banos" || info.tarea === "salon" || info.tarea === "cocina";
  const planDia = plan.find(x => x.dia === info.dia) || {};
  const alumnoId = esToggle ? null : planDia[`${info.tarea}_id`];
  const alumno = alumnoId ? alumnos.find(a => a.id_alumno === alumnoId) : null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
        {/* Cabecera */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#1e3a5f" }}>
              {TAREAS_ICONO[info.tarea]} {TAREAS_LABEL[info.tarea]}
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>{DIAS_LABEL[info.dia]}</div>
            {alumno && (
              <div style={{ fontSize: 13, color: "#3b82f6", marginTop: 4, fontWeight: 600 }}>
                🛏 {alumno.habitacion} · {alumno.nombre}
              </div>
            )}
          </div>
          <button onClick={onClose} style={styles.btnCerrarModal}>✕</button>
        </div>

        {/* Fotos */}
        {cargando ? (
          <p style={{ textAlign: "center", color: "#888" }}>Cargando…</p>
        ) : fotos.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px 0", color: "#9ca3af", fontSize: 14 }}>
            <div style={{ fontSize: 36, marginBottom: 6 }}>📷</div>
            No hay fotos para esta tarea
          </div>
        ) : (
          <>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#374151", marginBottom: 10 }}>📸 Fotos</div>
            {["antes", "despues"].map(tipo => {
              const fotosTipo = fotos.filter(f => f.tipo === tipo);
              if (fotosTipo.length === 0) return null;
              return (
                <div key={tipo} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                    {tipo === "antes" ? "📷 Antes" : "📸 Después"}
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {fotosTipo.map(f => (
                      <AuthImage
                        key={f.id}
                        src={`/servicio_fotos/${f.filename}`}
                        alt={tipo}
                        style={{ width: 90, height: 90, objectFit: "cover", borderRadius: 10, border: "2px solid #e2e8f0", cursor: "pointer" }}
                        onClick={() => setLightbox(`/servicio_fotos/${f.filename}`)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}
          onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
        >
          <AuthImage src={lightbox} alt="foto" style={{ maxWidth: "95vw", maxHeight: "90vh", borderRadius: 12, objectFit: "contain" }} />
          <button onClick={(e) => { e.stopPropagation(); setLightbox(null); }} style={{ position: "absolute", top: 18, right: 18, background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", borderRadius: 10, padding: "8px 14px", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   Estilos
───────────────────────────────────────────── */
const styles = {
  btnVolver: {
    padding: "8px 18px", borderRadius: 20,
    background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
    color: "#fff", border: "none", cursor: "pointer",
    fontWeight: 700, fontSize: 14, whiteSpace: "nowrap",
    boxShadow: "0 2px 6px rgba(29,78,216,0.3)",
  },
  cardPiso: {
    display: "flex", alignItems: "stretch", gap: 0,
    background: "#fff",
    borderRadius: 14, marginBottom: 16, overflow: "hidden",
    boxShadow: "0 2px 12px rgba(30,58,95,0.12)",
    border: "1px solid #e0e7ef",
  },
  cardPisoLeft: {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    gap: 6, padding: "14px 20px",
    background: "linear-gradient(160deg, #1e3a5f, #2563eb)",
    minWidth: 100,
  },
  abreviaturaGrande: {
    fontSize: 24, fontWeight: 900, color: "#fff",
    letterSpacing: "-0.5px", lineHeight: 1, textAlign: "center",
  },
  cardPisoDivider: {
    width: 1, background: "#e0e7ef",
  },
  cardPisoRight: {
    flex: 1, padding: "14px 18px", display: "flex", flexDirection: "column", justifyContent: "center",
  },
  badge: { padding: "4px 12px", borderRadius: 10, fontSize: 13, fontWeight: 700, display: "inline-block" },
  selectorSemana: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
    marginBottom: 18, background: "#f8fafc", borderRadius: 14,
    padding: "10px 14px", border: "1px solid #e2e8f0",
  },
  btnSemana: {
    padding: "8px 16px", borderRadius: 10, border: "none",
    background: "#e0e7ef", cursor: "pointer", fontSize: 16, fontWeight: 700, color: "#1e3a5f",
  },
  btnEditar: { padding: "7px 11px", borderRadius: 8, border: "none", background: "#eff6ff", cursor: "pointer", fontSize: 16 },
  btnToggle: { padding: "6px 14px", borderRadius: 10, border: "none", fontWeight: 700 },
  btnValidar: { padding: "5px 10px", borderRadius: 8, background: "#f0fdf4", color: "#15803d", fontWeight: 700, fontSize: 12, cursor: "pointer", border: "1px solid #86efac" },
  tabla: { width: "100%", borderCollapse: "collapse", boxShadow: "0 4px 18px rgba(0,0,0,0.1)", borderRadius: 14, overflow: "hidden" },
  th: { padding: "13px 10px", textAlign: "center", fontSize: 14, borderRight: "1px solid rgba(0,0,0,0.07)" },
  td: { padding: "11px 10px", textAlign: "center", borderBottom: "1px solid #f1f5f9", borderRight: "1px solid #f1f5f9", verticalAlign: "middle" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "16px" },
  modalBox: { background: "#fff", borderRadius: 20, width: "100%", maxWidth: 480, padding: "20px 16px 28px", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.25)" },
  opcionAlumno: { display: "flex", alignItems: "center", gap: 10, padding: "13px 14px", borderRadius: 12, marginBottom: 7, cursor: "pointer", border: "1px solid #e5e7eb", fontSize: 15, fontWeight: 500 },
  btnCerrarModal: { background: "#f3f4f6", border: "none", borderRadius: 8, padding: "5px 12px", cursor: "pointer", fontSize: 16, color: "#6b7280" },
};

export default OrganizarServicioPiso;
